import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { AppDispatch } from 'app/store';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';
import {
  createPortcallStatusLog,
  getPortcallStatusLog,
} from 'features/captains-form/slices/portCallSlice';
import { booleanOptionsForRadioGroup } from 'features/forms/helpers/random-form-helpers';
import { FormRefContext } from 'features/global/context/FormRefContext';

import {
  selectHealthDeclaration,
  updateHealthDeclaration,
} from '../slices/healthSlice';

import SanitaryMeasureModal from './SanitaryMeasureModal';
import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import PageHeader from '../../../../layout/components/PageHeader';
import ConfirmDeleteModal from '../../../../modals/components/ConfirmDeleteModal';
import Table, { DeleteModalData } from '../../../../tables/components/Table';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';
import { sanitaryMeasuresColumns } from '../utils/healthUtils';

import '../styles/health.scss';

const ThirdHealthStep = () => {
  const formRef = useRef<FormikProps<any>>(null);
  const { formContextRef } = useContext(FormRefContext);
  const params = useParams();
  const healthDeclaration = useSelector(selectHealthDeclaration);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditingTable, setIsEditingTable] = useState<boolean>(false);
  const [
    showConfirmDeleteAllMembersModal,
    setShowConfirmDeleteAllMembersModal,
  ] = useState<boolean>(false);
  const [confirmDeleteMemberModalData, setConfirmDeleteMemberModalData] =
    useState<DeleteModalData>({
      index: null,
      showModal: false,
    });

  const validationSchema = Yup.object({
    sanitaryMeasuresApplied: Yup.boolean(),
    sickAnimalOrPet: Yup.boolean(),
  });
  const isEditingRow = editingRow !== null;

  const dispatchValues = async (values: any) => {
    const isSuccess = await dispatch(
      updateHealthDeclaration({
        uid: params.uid as string,
        healthData: {
          ...values,
          stateParticulars: healthDeclaration.stateParticulars,
        },
      }) as any
    ).then((response: any) => {
      if (response.type && response.type.endsWith('/rejected')) return false;
      else return true;
    });

    if (!isSuccess) return;

    if (params.uid) {
      const isSuccessUpdateStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: DossierSectionOptions.HealthDeclaration,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdateStatus) return;

      dispatch(getPortcallStatusLog(params.uid));
    }
    navigate(`/${params.uid}/local-formalities`);
  };

  useEffect(() => {
    formContextRef.current = formRef.current;
  }, [formContextRef, formRef, healthDeclaration]);

  const handleSubmit = async (values: any) => {
    await dispatchValues(values);
  };

  const handleSanitaryMeasureData = async (data: any) => {
    const sanitaryMeasures = [...healthDeclaration.sanitaryMeasures];

    if (isEditingRow) {
      sanitaryMeasures[editingRow as number] = data;
    } else {
      sanitaryMeasures.push(data);
    }

    if (sanitaryMeasures && sanitaryMeasures.length > 0) {
      const isSuccess = await dispatch(
        updateHealthDeclaration({
          uid: params.uid || '',
          healthData: {
            hasSanitaryMeasuresBeenAppliedOnBoard: true,
            sanitaryMeasures: sanitaryMeasures,
          },
        }) as any
      ).then((response: any) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccess) return;
    }
  };

  const handleDeleteSingleMember = async () => {
    const sanitaryMeasures = [...healthDeclaration.sanitaryMeasures];
    sanitaryMeasures.splice(confirmDeleteMemberModalData.index as number, 1);
    const isSuccess = await dispatch(
      updateHealthDeclaration({
        uid: params.uid as string,
        healthData: {
          sanitaryMeasures: sanitaryMeasures,
        },
      }) as any
    ).then((response: any) => {
      if (response.type && response.type.endsWith('/rejected')) return false;
      else return true;
    });
    if (!isSuccess) return;

    setConfirmDeleteMemberModalData({
      index: null,
      showModal: false,
    });
  };

  const handleDeleteAllMembers = async () => {
    setShowConfirmDeleteAllMembersModal(false);

    const isSuccess = await dispatch(
      updateHealthDeclaration({
        uid: params.uid as string,
        healthData: {
          sanitaryMeasures: [],
        },
      }) as any
    ).then((response: any) => {
      if (response.type && response.type.endsWith('/rejected')) return false;
      else return true;
    });
    if (!isSuccess) return;
  };

  return (
    <div className="health-wrapper">
      <SanitaryMeasureModal
        isEditingRow={isEditingRow}
        editingRow={editingRow}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        setEditingRow={setEditingRow}
        setData={handleSanitaryMeasureData}
      />
      <ConfirmDeleteModal
        title={
          <h3>
            Are you sure you want to{' '}
            <span>
              delete all the <br />
              sanity measures
            </span>{' '}
            from the list?
          </h3>
        }
        show={showConfirmDeleteAllMembersModal}
        onCancelClick={() => setShowConfirmDeleteAllMembersModal(false)}
        onDeleteClick={() => handleDeleteAllMembers()}
        onCloseModal={() => setShowConfirmDeleteAllMembersModal(false)}
      />
      <ConfirmDeleteModal
        title={<h3>Are you sure you want to delete this sanity measure?</h3>}
        show={confirmDeleteMemberModalData.showModal}
        onCancelClick={() => {
          setConfirmDeleteMemberModalData({
            index: null,
            showModal: false,
          });
        }}
        onDeleteClick={() => handleDeleteSingleMember()}
        onCloseModal={() => setShowConfirmDeleteAllMembersModal(false)}
      />
      <PageHeader
        title={'Maritime declaration of health'}
        subtitle={'Health question - sanitary measures'}
      />
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        enableReinitialize
        initialValues={healthDeclaration}
        validationSchema={validationSchema}>
        {({ values }) => (
          <Form className="health-declaration-form">
            <div className="health-declaration-form__controls">
              <div className="health-declaration-form__row">
                <RadioButtonsCustom
                  name="hasSanitaryMeasuresBeenAppliedOnBoard"
                  label="7. Has a sanitary measure (e.g. quarantine, isolation, disinfection or decontamination) been applied on board?"
                  options={booleanOptionsForRadioGroup()}
                />
                {values.hasSanitaryMeasuresBeenAppliedOnBoard ? (
                  <Table
                    title={'Sanitary measures'}
                    addButtonText={'Add measure'}
                    data={healthDeclaration.sanitaryMeasures}
                    columns={sanitaryMeasuresColumns}
                    editingMode={isEditingTable}
                    setEditingMode={setIsEditingTable}
                    setIsAddModalOpen={setIsModalOpen}
                    setIsDeleteModalOpen={setShowConfirmDeleteAllMembersModal}
                    onEditButtonClick={(rowIndex) => {
                      setEditingRow(rowIndex);
                      setIsModalOpen(true);
                    }}
                    onDeleteButtonClick={(rowIndex) => {
                      setConfirmDeleteMemberModalData({
                        showModal: true,
                        index: rowIndex,
                      });
                    }}
                  />
                ) : null}
              </div>
              <div className="health-declaration-form__row">
                <RadioButtonsCustom
                  name="isThereASickAnimalOnBoard"
                  label="8. Is there a sick animal or pet on board?"
                  options={booleanOptionsForRadioGroup()}
                />
              </div>
            </div>
            <SkipAndNextButtons
              next={`/${params.uid}/local-formalities`}
              back={`/${params.uid}/health/second-step`}
              submitLabel="Submit"
              submitDisabled={false}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ThirdHealthStep;
